<template>
  <div v-if="shareConfig.shareable">
    <v-chip
      label
      class="share-chip-activator"
      color="white"
      variant="outlined"
      width="medium"
      :disabled="disabled || loading"
      @click="showModal = true"
    >
      <v-icon
        icon="$share-variant-outline"
        :color="useLighterColor ? 'iconDark' : 'iconDarkest'"
      />
    </v-chip>
    <Modal
      :show="showModal"
      hide-default-actions
      width="380px"
      :with-divider="showShareUsersWithAccess"
      @close="showModal = false; modelValue = null; note = ''"
    >
      <template #title>
        <h5>Share Report</h5>
      </template>
      <template #content>
        <ShareInput
          :model-value="modelValue"
          :loading="searchLoading"
          :items="autocompleteItems"
          @update:search="handleSearch"
          @update:model-value="handleModelValueUpdate"
        />
        <ShareUsersWithAccess
          v-if="showShareUsersWithAccess"
          class="mt-4"
          :users-with-access="shareConfig.usersWithAccess"
          @select-user="handleModelValueUpdate"
        />
        <ShareDSRAccessDetails
          v-if="modelValue?.type_key === TYPE_ACTIVE
            && !modelValue?.canDemographicSurveyReport
            && shareConfig.checkDSRAccess"
          class="mt-4"
          :user="modelValue"
        />
        <ShareNote v-if="modelValue?.type_key === TYPE_ACTIVE" v-model="note" class="mt-4" />
        <ShareLeadDetails v-if="modelValue?.type_key === TYPE_LEAD" class="mt-4" />
        <ShareThreeButtonAction
          v-if="shareConfig.showReportImageButton"
          class="mt-4"
          :disabled="!modelValue || loading"
          :share-via-email-text="shareViaEmailText"
          :share-via-image-text="shareViaImageText"
          @copy-report-link="copyReportLink"
          @share-via-email="($event) => { handleShareViaEmail($event); clickedButton = 'email'; }"
          @share-via-image="($event) => { handleShareViaImage($event); clickedButton = 'image'; }"
        />
        <ShareTwoButtonAction
          v-else
          class="mt-4"
          :disabled="!modelValue || loading"
          :share-via-email-text="shareViaEmailText"
          @copy-report-link="copyReportLink"
          @share-via-email="($event) => { handleShareViaEmail($event); clickedButton = 'email'; }"
        />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import Modal from '@/components/shared/Modal.vue';
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useInsightsShare } from '@/stores/insights/share';
import debounce from 'lodash-es/debounce';
import { searchTeamMembers } from '@/api/asyncSearch';
import { TYPE_ACTIVE, TYPE_LEAD } from '@/helpers/constants/userType';
import { writeToClipboardApi } from '@/helpers/copyToClipboard';
import { useAppSnackbar } from '@/stores/app/snackbar';
import ShareInput from '@/components/insights/ShareModal/ShareInput.vue';
import ShareUsersWithAccess from '@/components/insights/ShareModal/ShareUsersWithAccess.vue';
import ShareLeadDetails from '@/components/insights/ShareModal/ShareLeadDetails.vue';
import ShareNote from '@/components/insights/ShareModal/ShareNote.vue';
import ShareThreeButtonAction from '@/components/insights/ShareModal/ShareThreeButtonAction.vue';
import ShareTwoButtonAction from '@/components/insights/ShareModal/ShareTwoButtonAction.vue';
import ShareDSRAccessDetails from '@/components/insights/ShareModal/ShareDSRAccessDetails.vue';
import { useRoute } from 'vue-router';
import { useTracking } from '@/stores/tracking/tracking';
import { useInsightsExport } from '@/stores/insights/export';

const { shareConfig } = storeToRefs(useInsightsShare());
const {
  shareReportViaEmail,
  inviteUserToInsights,
  shareReportImage,
} = useInsightsShare();
const {
  insightsShareReportViaEmail,
  insightsShareReportImage,
  insightsCopiedReportLink,
} = useTracking();
const { updateFullReportExportInProgress } = useInsightsExport();

const route = useRoute();

const showModal = ref(false);
const modelValue = ref(null);
const autocompleteItems = ref([]);
const searchLoading = ref(false);
const loading = ref(false);
const clickedButton = ref('');
const note = ref('');

defineProps({
  disabled: { type: Boolean, default: false },
  useLighterColor: { type: Boolean, default: false },
});

const showShareUsersWithAccess = computed(() => Boolean(!modelValue.value
  && shareConfig.value.usersWithAccess?.length));

const shareViaEmailText = computed(() => {
  if (clickedButton.value === 'email' && loading.value) {
    return 'Sending...';
  }

  return 'Share via Email';
});

const shareViaImageText = computed(() => {
  if (clickedButton.value === 'image' && loading.value) {
    return 'Sending...';
  }

  return 'Email Report Image';
});

const shareUrl = computed(() => {
  if (['compliance-report', 'employer-brand-report', 'job-performance-report'].includes(route.name)) {
    const url = new URL(window.location.href);
    url.searchParams.set('share', 'true');

    return url.toString();
  }

  return window.location.href;
});

const debouncedSearch = debounce(async (query) => {
  if (!query) {
    autocompleteItems.value = [];
    return;
  }
  try {
    searchLoading.value = true;
    const response = await searchTeamMembers(query);
    autocompleteItems.value = response.users || [];
  } catch (e) {
    autocompleteItems.value = [];
  } finally {
    searchLoading.value = false;
  }
}, 300);

const handleSearch = (query) => {
  debouncedSearch(query);
};

const handleModelValueUpdate = (value) => {
  modelValue.value = typeof value !== 'string' ? value : null;
};

const copyReportLink = () => {
  try {
    writeToClipboardApi(shareUrl.value);

    insightsCopiedReportLink({
      reportName: route.name,
    });

    useAppSnackbar().addSuccessSnackbar({ text: 'Report link copied' });
  } catch (error) {
    useAppSnackbar().addErrorSnackbar({ text: 'Failed to copy report link' });
  } finally {
    showModal.value = false;
    modelValue.value = null;
    note.value = '';
  }
};

const handleShareViaEmail = async () => {
  const isActiveUser = modelValue.value?.type_key === TYPE_ACTIVE;

  loading.value = true;

  if (isActiveUser) {
    await shareReportViaEmail({
      userId: modelValue.value?.id,
      note: note.value,
      url: shareUrl.value,
      routeName: route.name,
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
    });
  } else {
    await inviteUserToInsights({
      email: modelValue.value?.email,
      id: modelValue.value?.id,
      routeName: route.name,
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
    });

    // Send five minutes later
    await shareReportViaEmail({
      userId: modelValue.value?.id,
      note: note.value,
      url: shareUrl.value,
      routeName: route.name,
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
      scheduledAt: 'in 5 min',
    });
  }

  insightsShareReportViaEmail({
    reportName: route.name,
    sharedUserStatus: modelValue.value?.type_key === TYPE_ACTIVE ? 'Active User' : 'New Invitation',
    noteIncluded: note.value ? 'Yes' : 'No',
  });

  loading.value = false;
  showModal.value = false;
  modelValue.value = null;
  note.value = '';
};

const handleShareViaImage = async () => {
  loading.value = true;
  updateFullReportExportInProgress(true);

  await shareReportImage({
    userId: modelValue.value?.id,
    note: note.value,
    page: route.name === 'requisition-report' ? 'requisition-report' : 'insights',
    routeName: route.name,
    name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
  });

  insightsShareReportImage({
    reportName: route.name,
    sharedUserStatus: modelValue.value?.type_key === TYPE_ACTIVE ? 'Active User' : 'New Invitation',
    noteIncluded: note.value ? 'Yes' : 'No',
  });

  loading.value = false;
  showModal.value = false;
  modelValue.value = null;
  note.value = '';

  updateFullReportExportInProgress(false);
};
</script>
<style scoped lang="less">
.share-chip-activator {
  width: 40px;
  height: 40px;
  justify-content: center;
  cursor: pointer;
  background: @bg-default;
  border: 1px solid @stroke-default;
  border-radius: 8px;

  :deep(.v-chip__overlay) {
    display: none;
  }

  &:hover,
  &[aria-expanded="true"] {
    background: @bg-brand-default !important;

    :deep(path) {
      fill: @white;
    }
  }
}

:deep(.v-btn--size-small) {
  height: 32px;
}

:deep(.header) {
  border-bottom: 1px solid @stroke-default;
}
</style>
